<script lang="ts" setup>
import { computed } from "vue";
import { useTranslations } from "@/composables/useTranslations";

const { t } = useTranslations();

const props = defineProps<{
    totalDomains: number;
    totalActiveDomains: number;
    totalInactiveDomains: number;
    emptyDomainsSlots: number;
    totalEmailsReported: number;
}>();

const tt = computed(() => ({
    domainsSlots: t("vue.domains_slots_tooltip"),
    activeDomains: t("vue.active_domains_tooltip"),
    inactiveDomains: t("vue.inactive_domains_tooltip"),
    emptySlots: t("vue.empty_slots_tooltip"),
}));
</script>

<template>
    <div class="domains-overview">
        <!-- LEFT BIG CARD -->
        <div class="stat-card left-big">
            <span class="stat-value">{{ totalDomains }}</span>
            <span class="stat-label">
                {{ t("vue.domains_slots") }}
                <button
                    type="button"
                    class="info-tooltip"
                    :data-tooltip="tt.domainsSlots"
                    :aria-label="tt.domainsSlots"
                    tabindex="0"
                >
                    <svg viewBox="0 0 20 20" aria-hidden="true">
                        <circle cx="10" cy="10" r="9"></circle>
                        <path d="M10 15v-5"></path>
                        <circle cx="10" cy="6.5" r="1"></circle>
                    </svg>
                </button>
            </span>
        </div>

        <!-- RIGHT BIG CARD WITH 3 MINI STATS -->
        <div class="stat-card right-big">
            <div class="mini-stats">
                <div class="mini-card">
                    <span class="stat-value">{{ totalActiveDomains }}</span>
                    <span class="stat-label">
                        {{ t("vue.active_domains") }}
                        <button
                            type="button"
                            class="info-tooltip"
                            :data-tooltip="tt.activeDomains"
                            :aria-label="tt.activeDomains"
                            tabindex="0"
                        >
                            <svg viewBox="0 0 20 20" aria-hidden="true">
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>
                    </span>
                </div>

                <div class="mini-card">
                    <span class="stat-value">{{ totalInactiveDomains }}</span>
                    <span class="stat-label">
                        {{ t("vue.inactive_domains") }}
                        <button
                            type="button"
                            class="info-tooltip"
                            :data-tooltip="tt.inactiveDomains"
                            :aria-label="tt.inactiveDomains"
                            tabindex="0"
                        >
                            <svg viewBox="0 0 20 20" aria-hidden="true">
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>
                    </span>
                </div>

                <div class="mini-card">
                    <span class="stat-value">{{ emptyDomainsSlots }}</span>
                    <span class="stat-label">
                        {{ t("vue.empty_slots") }}
                        <button
                            type="button"
                            class="info-tooltip"
                            :data-tooltip="tt.emptySlots"
                            :aria-label="tt.emptySlots"
                            tabindex="0"
                        >
                            <svg viewBox="0 0 20 20" aria-hidden="true">
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.domains-overview {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 16px;
    width: 100%;
}

/* CARD layout */
.stat-card {
    padding: 20px 24px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 10px;
}

.left-big {
    text-align: center;
}

/* RIGHT BIG CARD contains 3 inline mini-cards */
.right-big {
    display: flex;
    align-items: center;
}

.mini-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    width: 100%;
}

.mini-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

/* Shared typography */
.stat-value {
    color: #111827;
    font-size: 32px;
    font-weight: 600;
}

.stat-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 600;
}

/* Domains overview */
.domains-overview {
    display: grid;
    grid-template-columns: 1fr 3fr;
    gap: 16px;
    width: 100%;
}

/* LEFT */
.left-block .stat-card {
    height: 100%;
}

/* RIGHT – 3 cards inline inside one big card wrapper */
.right-block {
    display: flex;
    align-items: stretch;
}

.right-inner {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    width: 100%;
}

/* CARD STYLES */
.stat-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    padding: 18px 24px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
}

.stat-value {
    color: #111827;
    font-size: 32px;
    font-weight: 600;
    line-height: 1;
}

.stat-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 600;
}

/* --- TOOLTIP ICON --- */
.info-tooltip {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: default;
    outline: none;
}

.info-tooltip svg {
    width: 16px;
    height: 16px;
    stroke: #9ca3af;
    fill: none;
    stroke-width: 1.5;
}

/* TOOLTIP CONTENT */
.info-tooltip::after {
    content: attr(data-tooltip);
    position: absolute;
    left: 50%;
    top: calc(100% + 10px);
    transform: translateX(-50%);
    min-width: 150px;
    max-width: 260px;
    white-space: normal;
    padding: 10px 12px;
    border-radius: 8px;
    background: #111827;
    color: #fff;
    font-size: 12px;
    line-height: 1.2;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    opacity: 0;
    pointer-events: none;
    transition:
        opacity 0.15s ease-out,
        transform 0.15s ease-out;
    transform-origin: top center;
    z-index: 1;
}

/* INFO ARROW */
.info-tooltip::before {
    content: "";
    position: absolute;
    left: 50%;
    top: 100%;
    transform: translateX(-50%);
    border: 7px solid transparent;
    border-bottom-color: #111827;
    opacity: 0;
    transition: opacity 0.15s ease-out;
}

/* SHOW TOOLTIP ON HOVER */
.info-tooltip:hover::after,
.info-tooltip:focus-visible::after,
.info-tooltip:hover::before,
.info-tooltip:focus-visible::before {
    opacity: 1;
}

.info-tooltip:hover::after,
.info-tooltip:focus-visible::after {
    transform: translateX(-50%) translateY(2px);
}

/* MOBILE TOOLTIP SPACE */
@media (max-width: 640px) {
    .info-tooltip::after {
        max-width: 200px;
    }
}
</style>
